'use client';

import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import FeaturedCommunityMdSlideWrapper from '@new-components/atoms/FeaturedCommunityMdSlideWrapper';
import {ReactSlickCarouselType} from '@new-components/atoms/SlickCarousel';

import UpcomingEventsSlide from '@new-components/molecules/UpcomingEventsSlide';

import {featuredCarousel} from './constants';

import {DesktopCarousel, MobileCarousel} from './styles';

import {
  IsEnabledType,
  UpcomingEventsCarouselRef,
  UpcomingEventsTypes,
} from '@new-components/molecules/CommunityUpcomingEvents/types';

export interface UpcomingEventsCarouselProps {
  eventList: UpcomingEventsTypes[];
  onEnableChange: (state: IsEnabledType) => void;
}

const UpcomingEventsCarousel: React.FC<
  UpcomingEventsCarouselProps & {
    ref?: Ref<UpcomingEventsCarouselRef>;
  }
> = forwardRef<UpcomingEventsCarouselRef, UpcomingEventsCarouselProps>(
  (props, ref) => {
    const sliderRef = useRef<ReactSlickCarouselType>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const mdEventList = useMemo(() => {
      if (!props.eventList.length) {
        return [];
      }
      const listLength = props.eventList.length;
      const mdList: Array<Array<UpcomingEventsTypes>> = [];
      for (let i = 0; i < Math.ceil(listLength / 4); i++) {
        const start = i * 4;
        const end = start + 4;
        const sublist = props.eventList.slice(start, end);
        mdList.push(sublist);
      }
      return mdList;
    }, [props.eventList]);
    const totalSlides = mdEventList.length;

    useImperativeHandle(ref, () => ({
      next: () => sliderRef.current?.slickNext(),
      prev: () => sliderRef.current?.slickPrev(),
      isAtStart: currentSlide === 0,
      isAtEnd: currentSlide === totalSlides - 1,
    }));

    useEffect(() => {
      props.onEnableChange({
        prev: currentSlide === 0,
        next: currentSlide === totalSlides - 1,
      });
    }, []);

    const handleBeforeChange = (slide: React.SetStateAction<number>) => {
      setCurrentSlide(slide);
      props.onEnableChange({
        prev: slide === 0,
        next: slide === totalSlides - 1,
      });
    };

    return (
      <>
        <DesktopCarousel
          ref={sliderRef}
          {...{
            ...featuredCarousel.desktop,
            beforeChange: (_, next) => handleBeforeChange(next),
          }}>
          {mdEventList.map((subList, listIndex) => (
            <FeaturedCommunityMdSlideWrapper key={`md-list-${listIndex}`}>
              {subList.map((event, index) => (
                <UpcomingEventsSlide
                  key={`item-${listIndex}-${event.eventTitle}`}
                  date={event.date}
                  eventTitle={event.eventTitle}
                  image={event.image}
                  location={event.location}
                  month={event.month}
                  timestamp={event.timestamp}
                />
              ))}
            </FeaturedCommunityMdSlideWrapper>
          ))}
        </DesktopCarousel>
        <MobileCarousel {...featuredCarousel.mobile}>
          {props.eventList.map((event, listIndex) => (
            <UpcomingEventsSlide
              key={`item-${listIndex}-${event.eventTitle}`}
              date={event.date}
              eventTitle={event.eventTitle}
              image={event.image}
              location={event.location}
              month={event.month}
              timestamp={event.timestamp}
            />
          ))}
        </MobileCarousel>
      </>
    );
  }
);

export {default as UpcomingEventsSkeletonLoader} from './UpcomingEventsSkeletonLoader';

export default UpcomingEventsCarousel;
